import React from 'react';
import { Icon, Label, Segment } from 'semantic-ui-react';
import type { ContractLineAuditLogDto } from '../../services/contracts/ContractService';

export const LINE_FIELD_LABELS: Record<string, string> = {
  Negociacao: 'Negociação',
  Ddd: 'DDD',
  Numero: 'Número',
  ChipCcid: 'CCID',
  TipoVenda: 'Tipo de Venda',
  PlanId: 'Plano',
  Aparelho: 'Aparelho',
  ValorChip: 'Valor Chip',
  Quantidade: 'Quantidade',
  ExcluirDoContrato: 'Excluir do Contrato',
  RequerSuspensao: 'Requer Suspensão',
  TrocaLinha: 'Troca de Linha',
  SeraUsadaPeloCliente: 'Usada pelo Cliente',
  Protocolo: 'Protocolo',
  EhPortabilidade: 'Portabilidade',
  PortabilidadeNomeCompleto: 'Nome Portante',
  PortabilidadeCpf: 'CPF Portante',
  PortabilidadeOperadora: 'Operadora',
  PortabilidadeLinha: 'Linha a Portar',
  IsEsim: 'eSIM',
  HandsetDeviceId: 'Aparelho (catálogo)',
  ContractId: 'Subcontrato',
  AparelhoValorTotal: 'Valor Total Aparelho',
  AparelhoValorMensal: 'Valor Mensal Aparelho',
};

export const formatLineAuditDateTime = (dateString: string) => {
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} às ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
};

export const formatLineAuditValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (key === 'ValorTotalMensal' || key === 'ComprometimentoMinimo' || key === 'ValorChip') {
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  }
  if (Array.isArray(value)) {
    return `${value.length} item(ns)`;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${v}`);
    return entries.join(', ');
  }
  return String(value);
};

export const renderLineFieldChange = (
  field: string,
  oldVals: Record<string, unknown> | null,
  newVals: Record<string, unknown> | null,
  labels: Record<string, string> = LINE_FIELD_LABELS
) => {
  const label = labels[field] || field;
  const oldValue = oldVals?.[field];
  const newValue = newVals?.[field];

  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    const oldCount = oldValue.length;
    const newCount = newValue.length;
    const diff = newCount - oldCount;
    let summary = `${oldCount} → ${newCount}`;
    if (diff > 0) summary += ` (+${diff})`;
    else if (diff < 0) summary += ` (${diff})`;
    return (
      <div key={field} style={{ marginBottom: '8px' }}>
        <strong>{label}:</strong>{' '}
        <span style={{ color: '#666' }}>{summary}</span>
      </div>
    );
  }

  if (typeof oldValue === 'object' && oldValue !== null && !Array.isArray(oldValue)) {
    const subChanges: string[] = [];
    const allKeys = new Set([
      ...Object.keys(oldValue || {}),
      ...Object.keys((newValue as object) || {}),
    ]);
    allKeys.forEach((k) => {
      const ov = (oldValue as Record<string, unknown>)?.[k];
      const nv = (newValue as Record<string, unknown>)?.[k];
      if (String(ov ?? '') !== String(nv ?? '')) {
        subChanges.push(`${k}: ${ov || '-'} → ${nv || '-'}`);
      }
    });
    if (subChanges.length === 0) return null;
    return (
      <div key={field} style={{ marginBottom: '8px' }}>
        <strong>{label}:</strong>
        <div style={{ paddingLeft: '12px', fontSize: '0.9em', color: '#555' }}>
          {subChanges.map((c, i) => (
            <div key={i}>{c}</div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div key={field} style={{ marginBottom: '8px' }}>
      <strong>{label}:</strong>{' '}
      <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '6px' }}>
        {formatLineAuditValue(field, oldValue)}
      </span>
      <Icon name="arrow right" size="small" />
      <span style={{ fontWeight: 'bold', color: '#2185d0' }}>
        {formatLineAuditValue(field, newValue)}
      </span>
    </div>
  );
};

export const lineAuditActionLabel = (action: string) => {
  if (action === 'Create') return 'Criação';
  if (action === 'Update') return 'Edição';
  if (action === 'Delete') return 'Exclusão';
  return action;
};

interface LineAuditTimelineProps {
  logs: ContractLineAuditLogDto[];
  emptyMessage?: string;
}

export const LineAuditTimeline: React.FC<LineAuditTimelineProps> = ({
  logs,
  emptyMessage = 'Nenhuma alteração de linha registrada para este contrato.',
}) => {
  if (logs.length === 0) {
    return (
      <div style={{ color: '#666', fontSize: '0.95em' }}>
        <Icon name="info circle" />
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      {logs.map((log, index) => {
        let oldVals: Record<string, unknown> | null = null;
        let newVals: Record<string, unknown> | null = null;
        try {
          oldVals = log.oldValues ? JSON.parse(log.oldValues) : null;
        } catch {
          /* ignore */
        }
        try {
          newVals = log.newValues ? JSON.parse(log.newValues) : null;
        } catch {
          /* ignore */
        }

        return (
          <Segment key={log.id} style={{ position: 'relative', paddingLeft: '40px' }}>
            <div
              style={{
                position: 'absolute',
                left: '16px',
                top: '16px',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: index === 0 ? '#21ba45' : '#ccc',
              }}
            />
            {index < logs.length - 1 && (
              <div
                style={{
                  position: 'absolute',
                  left: '19px',
                  top: '28px',
                  width: '2px',
                  height: 'calc(100% - 28px)',
                  backgroundColor: '#e0e0e0',
                }}
              />
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div>
                <Label
                  size="small"
                  color={
                    log.action === 'Delete'
                      ? 'red'
                      : log.action === 'Create'
                        ? 'green'
                        : index === 0
                          ? 'blue'
                          : 'grey'
                  }
                >
                  <Icon
                    name={log.action === 'Delete' ? 'trash' : log.action === 'Create' ? 'plus' : 'edit'}
                  />
                  {lineAuditActionLabel(log.action)}
                </Label>
                <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>
                  {formatLineAuditDateTime(log.changedAt)}
                </span>
                {log.contractLineId && (
                  <span style={{ marginLeft: '8px', fontSize: '0.75em', color: '#999' }}>
                    Linha: {log.contractLineId.slice(0, 8)}…
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.85em', color: '#888' }}>
                <Icon name="user" />
                {log.changedByUserName || 'Desconhecido'}
                {log.changedByUserEmail && (
                  <span style={{ marginLeft: '4px' }}>({log.changedByUserEmail})</span>
                )}
              </div>
            </div>

            {log.changedFields.length > 0 ? (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '0.85em', color: '#888', marginBottom: '6px' }}>
                  {log.changedFields.length} campo(s) alterado(s):
                </div>
                {log.changedFields.map((field) => renderLineFieldChange(field, oldVals, newVals))}
              </div>
            ) : log.action === 'Create' && newVals ? (
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#555' }}>
                {Object.entries(newVals)
                  .filter(([k, v]) => k !== 'Id' && v !== null && v !== undefined && v !== '')
                  .slice(0, 6)
                  .map(([k, v]) => (
                    <div key={k}>
                      <strong>{LINE_FIELD_LABELS[k] || k}:</strong> {formatLineAuditValue(k, v)}
                    </div>
                  ))}
              </div>
            ) : log.action === 'Delete' && oldVals ? (
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#555' }}>
                Linha removida —{' '}
                {[oldVals.Ddd, oldVals.Numero, oldVals.ChipCcid].filter(Boolean).join(' / ') ||
                  'sem identificação'}
              </div>
            ) : (
              <div style={{ fontSize: '0.85em', color: '#888', fontStyle: 'italic' }}>
                Nenhuma alteração de campo detectada
              </div>
            )}
          </Segment>
        );
      })}
    </>
  );
};
